import {
  FARM_DIAGNOSTICS_PRACTICE_LABELS,
  FARM_YEAR_INPUTS_STORAGE_KEY,
} from 'constants/uiConstants';
import type {
  FarmYearInput,
  PracticeTag,
} from '../../../../redux/slices/InsightsSlice';

/** Keyed by farmId, then year — the shape of `farmYearInputs` in Redux. */
export type FarmYearInputsByFarm = Record<string, Record<number, FarmYearInput>>;

const KNOWN_PRACTICE_TAGS = new Set<string>(
  Object.keys(FARM_DIAGNOSTICS_PRACTICE_LABELS)
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isEmptyYearInput = (input: FarmYearInput): boolean =>
  input.rainfallMm == null && input.practiceTags.length === 0;

/**
 * One stored farm-year, or null when there's nothing usable in it. A bad
 * rainfall value or an unknown practice tag (e.g. one renamed in a later
 * release) is dropped on its own, keeping whatever else is valid.
 */
const parseYearInput = (value: unknown): FarmYearInput | null => {
  if (!isRecord(value)) return null;
  const { rainfallMm, practiceTags } = value;
  const input: FarmYearInput = {
    rainfallMm:
      typeof rainfallMm === 'number' && Number.isFinite(rainfallMm) && rainfallMm >= 0
        ? rainfallMm
        : null,
    practiceTags: Array.isArray(practiceTags)
      ? (practiceTags.filter(
          (tag): tag is PracticeTag =>
            typeof tag === 'string' && KNOWN_PRACTICE_TAGS.has(tag)
        ) as PracticeTag[])
      : [],
  };
  return isEmptyYearInput(input) ? null : input;
};

/**
 * Parses the stored JSON, keeping only valid farm-years. Missing, corrupt or
 * unexpected data gives an empty object rather than an error.
 */
export const parseStoredFarmYearInputs = (
  raw: string | null
): FarmYearInputsByFarm => {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!isRecord(parsed)) return {};

  const result: FarmYearInputsByFarm = {};
  for (const [farmId, years] of Object.entries(parsed)) {
    if (!farmId || !isRecord(years)) continue;
    for (const [yearKey, value] of Object.entries(years)) {
      const year = Number(yearKey);
      if (!Number.isInteger(year)) continue;
      const input = parseYearInput(value);
      if (input) (result[farmId] ??= {})[year] = input;
    }
  }
  return result;
};

/** Rainfall and management practice remembered in this browser, or {} when none (or storage is unavailable). */
export const loadFarmYearInputs = (): FarmYearInputsByFarm => {
  try {
    return parseStoredFarmYearInputs(
      localStorage.getItem(FARM_YEAR_INPUTS_STORAGE_KEY)
    );
  } catch {
    return {};
  }
};

/**
 * Remembers one farm-year. Reads the stored set first and changes only this
 * entry, so another tab's entries for other farm-years aren't overwritten.
 * An empty entry is removed rather than stored.
 */
export const storeFarmYearInput = (
  farmId: string,
  year: number,
  input: FarmYearInput | undefined
): void => {
  try {
    const stored = loadFarmYearInputs();
    const next = input ? parseYearInput(input) : null;
    if (next) {
      (stored[farmId] ??= {})[year] = next;
    } else if (stored[farmId]) {
      delete stored[farmId][year];
      if (Object.keys(stored[farmId]).length === 0) delete stored[farmId];
    }
    localStorage.setItem(FARM_YEAR_INPUTS_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Unavailable or full storage just means the entry lasts this session only.
  }
};
